import { useTranslation } from 'react-i18next';
import {
  type EntityOrSuggestionRef,
  type OrgFunctionKind,
  type PopulatedOrgFunction,
  type PopulatedSystem,
  type ProcessStepRecordingDisplay
} from '@bliks-io/contracts';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  TruncatedText,
  cn,
  lookupAssignee,
  isAssigneeDeleted,
  formatUserLifecycleSuffix,
  resolveUserLifecycle,
  getSystemLogoUrl,
  getUserAvatarUrl,
  getUserInitials,
  type AssigneeLookup,
  type UserLifecycle
} from '@bliks-io/ui';
import { ENTITY_ICONS, orgFunctionKindIconComponent } from '@/constants/entityIcons';
import { DELETED_ENTITY_CLASS } from '@/lib/useDeletedEntity';

/**
 * The optional "Helfer" columns for the process step table — the systems,
 * persons, functions and documents that support a step. Hidden by default and
 * revealed via the table's column-visibility menu, they give a high-level view
 * of which resources (notably which systems) are used where across a process.
 */

/** The glyph rendered for a helper entry — an avatar (persons), a logo
 *  (systems, with an icon fallback), a per-kind OrgFunction glyph or a plain
 *  document icon. */
type HelperMedia =
  | { kind: 'person'; avatarUrl?: string; initials: string }
  | { kind: 'orgFunction'; orgFunctionKind: OrgFunctionKind }
  | { kind: 'system'; logoUrl?: string }
  | { kind: 'document' };

/** One rendered row in a Helfer column. */
export interface HelperEntry {
  key: string;
  name: string;
  lifecycle: UserLifecycle | null;
  media: HelperMedia;
}

// Resolve a helper slot of unpopulated `{ type, id }` union refs against the
// org lookup. Pending-suggestion / unknown refs resolve to nothing and are
// skipped, matching the involved-entities aggregate rendered above the table.
// The lookup carries soft-deleted entities (with `deletedAt`), so resolved
// tombstones gray out.
function buildRefHelperEntries<T extends { _id: string; name?: string; deletedAt?: number }>(
  refs: EntityOrSuggestionRef[] | undefined,
  model: 'System' | 'OrgFunction',
  lookup: AssigneeLookup,
  toMedia: (entity: T) => HelperMedia
): HelperEntry[] {
  return (refs ?? []).flatMap((ref) => {
    const entity = lookupAssignee(ref, model, lookup) as T | undefined;
    if (!entity?.name) return [];
    return [
      {
        key: entity._id,
        name: entity.name,
        lifecycle: isAssigneeDeleted(entity) ? ('deleted' as const) : null,
        media: toMedia(entity)
      }
    ];
  });
}

export function buildHelperSystemEntries(
  step: ProcessStepRecordingDisplay,
  lookup: AssigneeLookup
): HelperEntry[] {
  return buildRefHelperEntries<PopulatedSystem>(step.helpers?.systems, 'System', lookup, (s) => ({
    kind: 'system',
    logoUrl: getSystemLogoUrl(s)
  }));
}

// Both OrgFunction kinds share this one slot (ADR 0004); the resolved `kind`
// only picks the row glyph.
export function buildHelperOrgFunctionEntries(
  step: ProcessStepRecordingDisplay,
  lookup: AssigneeLookup
): HelperEntry[] {
  return buildRefHelperEntries<PopulatedOrgFunction>(
    step.helpers?.orgFunctions,
    'OrgFunction',
    lookup,
    (fn) => ({ kind: 'orgFunction', orgFunctionKind: fn.kind })
  );
}

// Helper persons arrive already populated; their `deletedAt` is stripped on
// serialization, so resolve the lifecycle against the lookup instead.
export function buildHelperPersonEntries(
  step: ProcessStepRecordingDisplay,
  lookup: AssigneeLookup
): HelperEntry[] {
  return (step.helpers?.persons ?? []).flatMap((person) => {
    if (!person?._id) return [];
    const name = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
    if (!name) return [];
    return [
      {
        key: person._id,
        name,
        lifecycle: resolveUserLifecycle(lookup.users?.get(person._id) ?? person),
        media: {
          kind: 'person',
          avatarUrl: getUserAvatarUrl(person, 'thumbnail'),
          initials: getUserInitials(person.firstName, person.lastName)
        }
      }
    ];
  });
}

// Helper documents (Unterlagen) arrive already populated.
export function buildHelperDocumentEntries(step: ProcessStepRecordingDisplay): HelperEntry[] {
  return (step.helpers?.documents ?? []).flatMap((doc) => {
    if (!doc?._id || !doc.name) return [];
    return [{ key: doc._id, name: doc.name, lifecycle: null, media: { kind: 'document' } }];
  });
}

/** The icon for a helper entry that has no avatar of its own. OrgFunctions
 *  take the per-kind glyph (group for Teams, briefcase for Funktionen), the
 *  same mapping the OrgFunction pickers use. */
function helperMediaIcon(media: Exclude<HelperMedia, { kind: 'person' }>) {
  switch (media.kind) {
    case 'orgFunction':
      return orgFunctionKindIconComponent(media.orgFunctionKind);
    case 'system':
      return ENTITY_ICONS.system;
    case 'document':
      return ENTITY_ICONS.artifact;
  }
}

/** The small square glyph for a single helper entry, sized to sit inline next
 *  to its name in the list. */
function HelperMediaGlyph({ entry }: { entry: HelperEntry }) {
  const className = cn('h-6 w-6 shrink-0', entry.lifecycle === 'deleted' && DELETED_ENTITY_CLASS);

  if (entry.media.kind === 'person') {
    return (
      <Avatar className={className}>
        <AvatarImage src={entry.media.avatarUrl} alt={entry.name} />
        <AvatarFallback className="text-3xs">{entry.media.initials}</AvatarFallback>
      </Avatar>
    );
  }

  const Icon = helperMediaIcon(entry.media);
  return (
    <div
      className={cn(
        className,
        'rounded-lg bg-gray-2 flex items-center justify-center overflow-hidden'
      )}
    >
      {entry.media.kind === 'system' && entry.media.logoUrl ? (
        <img src={entry.media.logoUrl} alt={entry.name} className="h-full w-full object-contain" />
      ) : (
        <Icon className="h-3.5 w-3.5 text-gray-8" />
      )}
    </div>
  );
}

/**
 * Lists every helper entity of a step (systems / persons / functions /
 * documents) as a glyph + name row — the logo/avatar/kind/file icon next to its
 * name. Soft-deleted entities render grayed with the localized "(deleted)" marker.
 */
export function HelperListCell({ entries }: { entries: HelperEntry[] }) {
  const { t } = useTranslation();
  if (entries.length === 0) {
    return <span className="text-sm text-gray-8">{t('common.notAvailable')}</span>;
  }

  return (
    <div className="flex flex-col gap-1 max-w-xs">
      {entries.map((entry) => (
        <div
          key={entry.key}
          className={cn(
            'flex items-center gap-2 text-sm font-medium',
            entry.lifecycle === 'deleted' && DELETED_ENTITY_CLASS
          )}
        >
          <HelperMediaGlyph entry={entry} />
          <TruncatedText
            text={`${entry.name}${formatUserLifecycleSuffix(entry.lifecycle, t)}`}
            className="min-w-0 flex-1"
          />
        </div>
      ))}
    </div>
  );
}
